import { alpha, styled, useTheme } from '@mui/material'
import { Context, useCallback } from 'react'
import { DynamicTextTypography } from 'src/components/DynamicTextTypography'
import { DialogContextType } from 'src/enums'
import { useDialogCtx } from 'src/hooks/context-hooks'
import { MUIThemeColors } from 'src/mui-theme'
import { Flex } from 'src/styles/flexComponent'
import { getThemeColor } from 'src/utils'
import { getMainComponentName } from 'src/utils/function'
import { EquipmentDetailModal } from '../EquipmentDetailModal'

interface IActivityLogEquipmentLinkProps {
  metaData?: Record<string, any> | null
  customerId: EntityId
  localityId?: EntityId
  context?: Context<any> | null
  permission?: number
  pop?: () => void
}

export const ActivityLogEquipmentLink: React.FC<IActivityLogEquipmentLinkProps> = ({
  metaData,
  customerId,
  localityId,
  context,
  permission,
  pop,
}) => {
  const { showDialog } = useDialogCtx()
  const theme = useTheme()

  const equipmentId = metaData?.['movedToEquipmentId'] ?? metaData?.['movedFromEquipmentId']
  const equipmentName = metaData?.['movedToEquipmentName'] ?? metaData?.['movedFromEquipmentName']
  const equipmentTypeId = metaData?.['movedToEquipmentTypeId'] ?? metaData?.['movedFromEquipmentTypeId']

  const onEquipmentClick = useCallback(() => {
    if (!equipmentId || !equipmentTypeId) return
    pop?.()
    showDialog(
      EquipmentDetailModal,
      {
        componentProps: {
          customerId: customerId as EntityId,
          localityId: localityId as EntityId,
          permission: permission,
          context: context,
          mainComponentId: equipmentId,
          mainComponentTypeId: equipmentTypeId,
          viewingLocalityId: localityId as EntityId,
        },
        opts: {
          maxWidth: false,
          fullWidth: true,
          bgColor: getThemeColor(theme, MUIThemeColors.backgroundMain),
          rootWidth: '1400px',
        },
        sx: { maxHeight: '100%' },
      },
      DialogContextType.localityContext,
      true,
    )
  }, [customerId, localityId, permission, context, equipmentId, equipmentTypeId, showDialog, theme])

  if (!equipmentName) return <>-</>

  const isClickable = Boolean(equipmentId && equipmentTypeId)

  return (
    <Flex.Row justifyContent="flex-start" alignItems="center">
      <EquipmentLink
        text={getMainComponentName(equipmentName)}
        maxLength={20}
        hasId={isClickable}
        fontWeight={600}
        onClick={() => isClickable && onEquipmentClick()}
      />
    </Flex.Row>
  )
}

const EquipmentLink = styled(DynamicTextTypography, {
  shouldForwardProp: prop => prop !== 'hasId',
})<{ hasId: boolean }>(({ theme, hasId }) => ({
  textDecoration: hasId ? 'underline' : 'none',
  textUnderlineOffset: hasId ? '8px' : undefined,
  cursor: hasId ? 'pointer' : 'default',
  color: hasId
    ? alpha(getThemeColor(theme, MUIThemeColors.primaryMain), 0.8)
    : getThemeColor(theme, MUIThemeColors.black),
}))
